import { Box } from '@mui/material';
import { ArrowsOut } from '@phosphor-icons/react';
import { Editor } from '@tinymce/tinymce-react';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Position, useEdges, useReactFlow } from 'reactflow';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { arrayDataToText } from '../../util/dataToText';
import { isActiveEdge } from '../../util/findActiveHandle';
import { NodeProps } from '../../util/Types/NodeProps';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import { useNodeResize } from '../../hooks/useNodeResize';
import InputHandle from '../UI/InputHandle/InputHandle';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';

const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 400;
const MIN_WIDTH = 200;
const MIN_HEIGHT = 200;

function RichTextBoxNode({ data, type, id, selected, width, height }: NodeProps) {
  const { user, setAuthUser } = useAuthStore(state => state);
  const editorRef = useRef(null);
  const [whoIsChanging, setWhoIsChanging] = useState<any>({
    isChangingDirectly: null,
    value: '',
  });

  const { flowId } = useParams();
  const edges = useEdges();
  const { setNodes: setNodeState } = useReactFlow();

  const { setSaving, setSchema } = useFlowsStore(state => state);

  const [myValue, setMyValue] = useState('');

  const saveData = useCallback(
    (payload: any, changeType: string = 'default') => {
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowId,
        objectData: payload,
        setSchema,
        changeType,
      });
    },
    [edges, flowId, id, setNodeState, setSaving, setSchema],
  );

  const {
    nodeWidth,
    nodeHeight,
    handleResizeStart,
    isResizing,
  } = useNodeResize({
    id,
    data,
    width: width ?? undefined,
    height: height ?? undefined,
    defaultWidth: DEFAULT_WIDTH,
    defaultHeight: DEFAULT_HEIGHT,
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    onResize: useCallback(
      (newWidth: number, newHeight: number) => {
        const padding = 32;
        const editorWidth = newWidth - padding;
        const editorHeight = newHeight - padding;

        const getElement: any = document?.querySelectorAll('.tox-tinymce');
        if (getElement && getElement.length > 0) {
          const nodeElement = document.querySelector(`[data-id="${id}"]`);
          if (nodeElement) {
            Array.from(getElement).forEach((editor: any) => {
              if (nodeElement.contains(editor)) {
                editor.style.width = editorWidth + 'px';
                editor.style.height = editorHeight + 'px';
              }
            });
          }
        }
      },
      [id],
    ),
    onResizeEnd: useCallback(
      (finalWidth: number, finalHeight: number) => {
        saveData({
          size: { width: finalWidth, height: finalHeight },
          text: whoIsChanging.value || data?.text || '',
        });
      },
      [saveData, whoIsChanging.value, data?.text],
    ),
  });

  useDebounceEffect(
    () => {
      if (!whoIsChanging.isChangingDirectly) {
        setMyValue(
          data?.['textData']?.length > 0
            ? arrayDataToText(data?.['textData'])
            : data?.text,
        );
      }
    },
    [data.text, data?.textData, whoIsChanging.isChangingDirectly],
    300,
  );

  const handleEditorChange = useCallback(
    (content: React.SetStateAction<string>, editor: any) => {
      try {
        if ((data?.['textData'] || [])?.length === 0) {
          setMyValue(content);
          setWhoIsChanging({
            isChangingDirectly: true,
            value: content as string,
          });
        } else {
          editor.setContent(myValue);
        }
      } catch (error) {
        console.log('error', error);
      }
    },
    [edges.length, data?.['textData'], myValue],
  );


  useDebounceEffect(
    () => {
      if (whoIsChanging.isChangingDirectly && !isResizing) {
        saveData({
          text: whoIsChanging.value,
          size: {
            height: nodeHeight,
            width: nodeWidth,
          },
        }, 'changeText');
        setWhoIsChanging({
          isChangingDirectly: false,
          value: '',
        });
      }
    },
    [whoIsChanging?.isChangingDirectly, whoIsChanging?.value, nodeWidth, nodeHeight, saveData, isResizing],
    500,
  );

  useEffect(() => {
    if (!selected) {
      const getElement: any = document?.querySelectorAll(
        '.tox-toolbar__overflow',
      );
      const toxMenu = document?.querySelectorAll('.tox-menu');
      if (getElement) {
        getElement.forEach((item: any) => {
          item.style.display = 'none';
        });
      }
      if (toxMenu) {
        toxMenu.forEach((item: any) => {
          item.style.display = 'none';
        });
      }
    }
  }, [selected]);

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;

  return (
    <Node
      id={id}
      data={data}
      isLoading={false}
      type={type}
      showTokensUsed={false}
    >
      {!data?.collapsed && (
        <>
          <Box
            p="16px"
            sx={{
              width: `${nodeWidth}px`,
              height: `${nodeHeight}px`,
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <InputHandle
              activeColor={findColor?.color}
              handleId={'input'}
              isActive={isActiveEdge(edges, id, 'input', 'target')}
              left={-12}
              top={'50%'}
              position={Position.Left}
              type="target"
            />

            <InputHandle
              activeColor={findColor?.color}
              handleId={'textOutput'}
              isActive={isActiveEdge(edges, id, 'textOutput', 'source')}
              right={-12}
              top={'50%'}
              position={Position.Right}
              type="source"
            />

            <Box
              sx={{
                pointerEvents: isResizing ? 'none' : 'auto',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <Editor
                key={user?.theme}
                id={data.myId}
                apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
                cloudChannel="5"
                ref={editorRef}
                scriptLoading={{
                  async: true,
                }}
                value={myValue}
                init={{
                  branding: false,
                  skin: user?.theme === 'dark' ? 'oxide-dark' : '',
                  content_css: user?.theme === 'dark' ? 'dark' : '',
                  resize: false,
                  width: '100%',
                  height: '100%',
                  plugins: [
                    'advlist autolink lists link image',
                    'wordcount',
                    'paste',
                    'code',
                    'table',
                    'charmap print preview anchor help',
                    'searchreplace visualblocks code',
                    'insertdatetime media table paste wordcount',
                  ],
                  toolbar:
                    'undo redo | formatselect | bold italic | \
                alignleft aligncenter alignright | \
                bullist numlist outdent indent | wordcount | code | tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
                }}
                onEditorChange={handleEditorChange}
              />
            </Box>
            {selected && (
              <Box
                onMouseDown={handleResizeStart}
                className="nodrag"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: findColor?.color || '#3650DE',
                  borderRadius: '4px',
                  cursor: 'nwse-resize',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: findColor?.color || '#3650DE',
                    opacity: 0.9,
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <ArrowsOut
                  size={14}
                  color="#fff"
                  weight="bold"
                  style={{ transform: 'rotate(45deg)' }}
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </Node>
  );
}

export default memo(RichTextBoxNode);
